(() => {
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const theme = window.JataraoTheme || {};
  const routes = theme.routes || {};
  const toast = document.querySelector('[data-toast]');
  let toastTimer;
  let activeLayer;

  const showToast = (message) => {
    if (!toast || !message) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 3200);
  };

  const setExpanded = (control, expanded) => {
    if (control) control.setAttribute('aria-expanded', String(expanded));
  };

  const openLayer = (layer, control, bodyClass) => {
    if (!layer) return;
    layer.hidden = false;
    setExpanded(control, true);
    document.body.classList.add(bodyClass);
    activeLayer = layer;
    const focusTarget = layer.querySelector('input, button, a[href]');
    window.requestAnimationFrame(() => focusTarget?.focus());
  };

  const closeLayer = (layer, control, bodyClass) => {
    if (!layer) return;
    layer.hidden = true;
    setExpanded(control, false);
    document.body.classList.remove(bodyClass);
    if (activeLayer === layer) activeLayer = undefined;
    control?.focus();
  };

  const menu = document.querySelector('[data-mobile-drawer]');
  const menuOverlay = document.querySelector('[data-drawer-overlay]');
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const menuClose = document.querySelector('[data-menu-close]');

  const openMenu = () => {
    openLayer(menu, menuToggle, 'menu-open');
    if (menuOverlay) menuOverlay.hidden = false;
  };
  const closeMenu = () => {
    closeLayer(menu, menuToggle, 'menu-open');
    if (menuOverlay) menuOverlay.hidden = true;
  };

  menuToggle?.addEventListener('click', openMenu);
  menuClose?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);

  const searchPanel = document.querySelector('[data-search-panel]');
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchClose = document.querySelector('[data-search-close]');
  searchToggle?.addEventListener('click', () => openLayer(searchPanel, searchToggle, 'search-open'));
  searchClose?.addEventListener('click', () => closeLayer(searchPanel, searchToggle, 'search-open'));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (menu && !menu.hidden) closeMenu();
      if (searchPanel && !searchPanel.hidden) closeLayer(searchPanel, searchToggle, 'search-open');
      const openDialog = document.querySelector('dialog[open]');
      if (openDialog) openDialog.close();
      return;
    }

    if (event.key !== 'Tab' || !activeLayer) return;
    const focusable = [...activeLayer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.querySelectorAll('[data-quantity-change]').forEach((button) => {
    button.addEventListener('click', () => {
      const wrapper = button.closest('[data-quantity]');
      const input = wrapper?.querySelector('[data-quantity-input]');
      if (!input) return;
      const delta = Number(button.dataset.quantityChange || 0);
      const minimum = Number(input.min || 1);
      const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const nextValue = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + delta));
      input.value = String(nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  const updateCartCount = async () => {
    if (!routes.cart) return;
    const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
  };

  document.querySelectorAll('[data-ajax-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!event.submitter?.hasAttribute('data-add-to-cart')) return;
      event.preventDefault();
      const submitButton = form.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled) return;
      const originalLabel = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form)
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || theme.strings?.addError);
        await updateCartCount();
        showToast(theme.strings?.added);
      } catch (error) {
        showToast(error.message || theme.strings?.addError);
      } finally {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.textContent = originalLabel;
      }
    });
  });

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(theme.locale || 'en-IN', {
        style: 'currency',
        currency: theme.currency || 'INR',
        currencyDisplay: theme.moneyWithCurrency ? 'code' : 'symbol',
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount);
    } catch (_error) {
      return `₹${amount.toFixed(2)}`;
    }
  };

  document.querySelectorAll('[data-cart-whatsapp]').forEach((button) => {
    button.addEventListener('click', async () => {
      button.setAttribute('aria-busy', 'true');
      const whatsappWindow = window.open('about:blank', '_blank');
      if (whatsappWindow) whatsappWindow.opener = null;
      try {
        const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(theme.strings?.addError);
        const cart = await response.json();
        if (!cart.items?.length) {
          whatsappWindow?.close();
          return;
        }
        const lines = cart.items.map((item) => {
          const variant = item.variant_title && item.variant_title !== 'Default Title' ? ` (${item.variant_title})` : '';
          const properties = Object.entries(item.properties || {})
            .filter(([key, value]) => value && !key.startsWith('_'))
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          const propertyLine = properties ? `\n  ${properties}` : '';
          return `• ${item.product_title}${variant} × ${item.quantity} — ${money(item.final_line_price)}${propertyLine}`;
        });
        const noteLines = cart.note
          ? ['', `${theme.strings?.whatsappCartNote || 'Order note'}: ${cart.note}`]
          : [];
        const message = [
          theme.strings?.whatsappCartIntro || 'Hello JĀTARAO! 👋 I would like to order these items:',
          '',
          ...lines,
          ...noteLines,
          '',
          (theme.strings?.whatsappCartTotal || 'Cart total: __TOTAL__').replace('__TOTAL__', money(cart.total_price)),
          theme.strings?.whatsappCartConfirm || 'Please confirm availability, delivery/pickup details, and the final payable amount.'
        ].join('\n');
        const number = String(theme.whatsappNumber || '').replace(/\D/g, '');
        const whatsappUrl = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
        if (whatsappWindow) whatsappWindow.location.href = whatsappUrl;
        else window.location.assign(whatsappUrl);
      } catch (error) {
        whatsappWindow?.close();
        showToast(error.message || theme.strings?.addError);
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  });

  document.querySelectorAll('[data-filter-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const filters = button.closest('[data-filters]');
      if (!filters) return;
      const isOpen = filters.classList.toggle('is-open');
      setExpanded(button, isOpen);
    });
  });

  document.querySelectorAll('[data-filter-input]').forEach((input) => {
    input.addEventListener('change', () => input.closest('form')?.submit());
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const form = select.closest('form') || document.getElementById(select.getAttribute('form'));
      form?.submit();
    });
  });

  document.querySelectorAll('[data-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.closest('form')?.submit());
  });

  document.querySelectorAll('select[data-default]').forEach((select) => {
    const requestedValue = select.dataset.default;
    if (!requestedValue) return;
    const matchingOption = [...select.options].find((option) => (
      option.value === requestedValue || option.textContent.trim() === requestedValue
    ));
    if (matchingOption) matchingOption.selected = true;
  });

  document.querySelectorAll('[data-media-dialog-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const dialog = document.getElementById(button.getAttribute('aria-controls'));
      if (typeof dialog?.showModal !== 'function') return;
      dialog.showModal();
      document.body.classList.add('dialog-open');
    });
  });

  document.querySelectorAll('[data-media-dialog]').forEach((dialog) => {
    dialog.addEventListener('close', () => document.body.classList.remove('dialog-open'));
    dialog.querySelector('[data-dialog-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });

  document.querySelectorAll('product-recommendations[data-url]').forEach(async (element) => {
    if (element.dataset.loaded === 'true') return;
    try {
      const response = await fetch(element.dataset.url);
      if (!response.ok) return;
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const replacement = html.querySelector('product-recommendations');
      if (replacement?.innerHTML.trim()) element.innerHTML = replacement.innerHTML;
      element.dataset.loaded = 'true';
    } catch (_error) {
      element.dataset.loaded = 'error';
    }
  });

  document.querySelectorAll('[data-care-tabs]').forEach((tabsRoot) => {
    const tabs = [...tabsRoot.querySelectorAll('[data-care-tab]')];
    const panels = [...tabsRoot.querySelectorAll('[data-care-panel]')];
    if (!tabs.length || !panels.length) return;

    const activateTab = (name, moveFocus = false) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.careTab === name;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        if (isActive && moveFocus) tab.focus();
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.carePanel !== name;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab.dataset.careTab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activateTab(tabs[nextIndex].dataset.careTab, true);
      });
    });

    const hashTopic = window.location.hash.replace('#', '');
    if (tabs.some((tab) => tab.dataset.careTab === hashTopic)) activateTab(hashTopic);
  });

  const revealElements = document.querySelectorAll('.reveal');
  if (!revealElements.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    revealElements.forEach((element) => element.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  revealElements.forEach((element) => observer.observe(element));
})();
